/**
 * Scoped Query Helper
 *
 * Builds WHERE conditions with company scope, branch scope, date filters, search,
 * then executes count + paginated query.
 *
 * Route handlers only need to:
 * 1. Define their specific filters
 * 2. Call scopedQuery()
 * 3. Enrich the results (joins/batch loads)
 *
 * IMPORTANT: Never use sql.raw() for sort columns — always use the `allowed` whitelist.
 */

import { eq, and, or, like, sql, asc, desc, isNull, inArray, type SQL } from "drizzle-orm";
import type { Column } from "drizzle-orm";
import type { ScopeType } from "../types/module";
import { applyBranchScope } from "./apply-branch-scope";
import type { BranchScopeConfig, BranchScopeContext } from "./branch-scope";
import { isRecordInBranchScope } from "./branch-scope";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Record scope restriction */
export interface RecordScope {
  type: ScopeType;
  userId: string;
  assignedField?: string;  // column name from doctype config
}

export interface ScopedQueryOptions<TTable extends Record<string, any>> {
  /** Drizzle instance */
  db: any;
  /** Drizzle table reference */
  table: TTable;

  /** Required: company_id filter */
  companyId: string;

  /** Additional WHERE conditions from route handler */
  conditions?: SQL[];

  /** Branch scope config (from module doctype definition) */
  branchScope?: BranchScopeConfig;
  /** Branch scope context (user's allowed branches + active selection) */
  branchContext?: BranchScopeContext;

  /** Soft delete: add isNull(deletedAt) if the table has a deletedAt column */
  softDelete?: boolean;

  /** Pagination */
  page?: number;
  perPage?: number;

  /** Sorting */
  sort?: {
    /** Column name from query params */
    column?: string;
    /** Sort direction */
    direction?: "asc" | "desc";
    /** Map of allowed column names to Drizzle column references */
    allowed: Record<string, Column>;
    /** Default column if not specified or not in allowed list */
    default: Column;
  };

  /**
   * Date filtering (mirrors applyDateFilter from core/utils/date-filter.ts)
   *
   * Uses the same convention: dateColumn selects which column,
   * dateFrom/dateTo define the range. dateTo gets "T23:59:59.999Z" appended.
   */
  dateFilter?: {
    /** Selected date column key (defaults to "createdAt") */
    column?: string;
    /** Start date (ISO string) */
    from?: string;
    /** End date (ISO string, time will be appended to cover full day) */
    to?: string;
    /** Map of allowed date column names to Drizzle column references */
    allowed: Record<string, Column>;
  };

  /** Text search (OR across multiple columns) */
  search?: {
    /** Search term from query params */
    term?: string;
    /** Drizzle column references to search in */
    columns: Column[];
  };

  /** Record scope restriction */
  scope?: RecordScope;

  /**
   * Document sharing: include records shared with this user.
   * Pass pre-fetched shared record IDs (from getSharedRecordIds).
   * Records in this list will be included even if outside scope.
   */
  sharedRecordIds?: string[];
}

export interface ScopedQueryResult<TRow> {
  data: TRow[];
  meta: {
    page: number;
    perPage: number;
    total: number;
  };
}

// ---------------------------------------------------------------------------
// scopedQuery — paginated list with scoping
// ---------------------------------------------------------------------------

export async function scopedQuery<TTable extends Record<string, any>>(
  options: ScopedQueryOptions<TTable>,
): Promise<ScopedQueryResult<any>> {
  const {
    db,
    table,
    companyId,
    conditions: extraConditions = [],
    branchScope,
    branchContext,
    softDelete,
    page = 1,
    perPage = 20,
    sort,
    dateFilter,
    search,
    scope,
    sharedRecordIds,
  } = options;

  // Build conditions array
  const conditions: SQL[] = [];

  // 1. Company filter (always)
  if ((table as any).companyId) {
    conditions.push(eq((table as any).companyId, companyId));
  }

  // 2. Soft delete filter — auto-detect if table has deletedAt column
  const shouldSoftDelete = softDelete ?? !!(table as any).deletedAt;
  if (shouldSoftDelete && (table as any).deletedAt) {
    conditions.push(isNull((table as any).deletedAt));
  }

  // 3. Branch scope + Record scope + Sharing override
  // If sharing is enabled, wrap scope conditions: (scope) OR (id IN sharedIds)
  const scopeConditions: SQL[] = [];

  if (branchScope && branchContext) {
    const branchFilter = applyBranchScope(table, branchScope, branchContext);
    if (branchFilter) {
      scopeConditions.push(branchFilter);
    }
  }

  // 4. Scope filter (own / own_and_assigned / assigned_only)
  if (scope && scope.type !== "all") {
    const scopeOrConditions: SQL[] = [];

    if ((scope.type === "own" || scope.type === "own_and_assigned") && (table as any).createdBy) {
      scopeOrConditions.push(eq((table as any).createdBy, scope.userId));
    }

    if ((scope.type === "own_and_assigned" || scope.type === "assigned_only") && scope.assignedField) {
      const assignedCol = (table as any)[scope.assignedField];
      if (assignedCol) {
        scopeOrConditions.push(eq(assignedCol, scope.userId));
      }
    }

    // If assigned_only but no assignedField configured, fallback to own
    if (scope.type === "assigned_only" && !scope.assignedField && (table as any).createdBy) {
      scopeOrConditions.push(eq((table as any).createdBy, scope.userId));
    }

    if (scopeOrConditions.length > 0) {
      const scopeFilter = scopeOrConditions.length === 1
        ? scopeOrConditions[0]
        : or(...scopeOrConditions)!;
      scopeConditions.push(scopeFilter);
    }
  }

  // Combine scope conditions, with sharing override
  if (scopeConditions.length > 0) {
    const scopeFilter = scopeConditions.length === 1
      ? scopeConditions[0]
      : and(...scopeConditions)!;

    if (sharedRecordIds && sharedRecordIds.length > 0 && (table as any).id) {
      // (scope) OR (id IN sharedRecordIds) — sharing overrides scope
      conditions.push(
        or(scopeFilter, inArray((table as any).id, sharedRecordIds))!,
      );
    } else {
      conditions.push(scopeFilter);
    }
  } else if (sharedRecordIds && sharedRecordIds.length > 0 && (table as any).id) {
    // No scope conditions but sharing is provided — no additional filter needed
    // (all records are already visible, sharing doesn't restrict)
  }

  // 5. Date filter (matches applyDateFilter convention from core/utils/date-filter.ts)
  if (dateFilter && (dateFilter.from || dateFilter.to)) {
    const col = dateFilter.allowed[dateFilter.column ?? "createdAt"];
    if (col) {
      if (dateFilter.from) {
        conditions.push(sql`${col} >= ${dateFilter.from}`);
      }
      if (dateFilter.to) {
        // Validate ISO date format (YYYY-MM-DD), then pad to end-of-day
        const toDateOnly = /^\d{4}-\d{2}-\d{2}/.exec(dateFilter.to)?.[0];
        if (toDateOnly) {
          conditions.push(sql`${col} <= ${toDateOnly + "T23:59:59.999Z"}`);
        }
      }
    }
  }

  // 6. Search (OR across columns, escape LIKE wildcards)
  if (search?.term && search.columns.length > 0) {
    const escaped = search.term.replace(/[%_]/g, "\\$&");
    const searchTerm = `%${escaped}%`;
    const searchConditions = search.columns.map((col) => like(col, searchTerm));
    if (searchConditions.length === 1) {
      conditions.push(searchConditions[0]);
    } else {
      conditions.push(or(...searchConditions)!);
    }
  }

  // 7. Extra conditions from route handler
  conditions.push(...extraConditions);

  // Build WHERE clause
  const whereClause =
    conditions.length > 0 ? and(...conditions) : undefined;

  // Sort
  let orderByClause;
  if (sort) {
    const col =
      (sort.column && sort.allowed[sort.column]) || sort.default;
    orderByClause = sort.direction === "asc" ? asc(col) : desc(col);
  } else if ((table as any).createdAt) {
    // Default: createdAt DESC
    orderByClause = desc((table as any).createdAt);
  }

  // Execute count + data queries in parallel
  const offset = (page - 1) * perPage;
  let dataQuery = db.select().from(table).where(whereClause);
  if (orderByClause) {
    dataQuery = dataQuery.orderBy(orderByClause);
  }

  const [countResult, data] = await Promise.all([
    db.select({ count: sql<number>`count(*)` }).from(table).where(whereClause).get(),
    dataQuery.limit(perPage).offset(offset).all(),
  ]);
  const total = countResult?.count ?? 0;

  return {
    data,
    meta: { page, perPage, total },
  };
}

// ---------------------------------------------------------------------------
// scopedGet — single record with scope validation
// ---------------------------------------------------------------------------

export interface ScopedGetOptions<TTable extends Record<string, any>> {
  /** Drizzle instance */
  db: any;
  /** Drizzle table reference */
  table: TTable;
  /** Record ID */
  id: string;
  /** Company ID for scope validation */
  companyId: string;
  /** Branch scope config */
  branchScope?: BranchScopeConfig;
  /** Branch scope context */
  branchContext?: BranchScopeContext;
  /** Record scope restriction */
  scope?: RecordScope;
  /**
   * Document sharing: pre-fetched shared record IDs for this user.
   * If the record ID is in this list, scope restrictions are overridden.
   */
  sharedRecordIds?: string[];
}

/**
 * Fetch a single record with scope validation.
 * Returns the record or null if not found / not in scope.
 *
 * IMPORTANT: companyId is required — the caller must provide it
 * (from query params or by looking up the record first).
 */
export async function scopedGet<TTable extends Record<string, any>>(
  options: ScopedGetOptions<TTable>,
): Promise<any | null> {
  const { db, table, id, companyId, branchScope, branchContext, scope, sharedRecordIds } = options;

  const record = await db
    .select()
    .from(table)
    .where(
      and(
        eq((table as any).id, id),
        eq((table as any).companyId, companyId),
      ),
    )
    .get();

  if (!record) return null;

  // Soft delete: if table has deletedAt and record is soft-deleted, treat as not found
  if ((table as any).deletedAt && record.deletedAt) {
    return null;
  }

  // Check if record is shared with this user — if so, skip scope restrictions
  const isSharedWithUser = sharedRecordIds && sharedRecordIds.includes(id);
  if (isSharedWithUser) {
    return record; // Sharing overrides scope
  }

  // Branch scope validation
  if (branchScope && branchContext) {
    if (!isRecordInBranchScope(record, branchScope, branchContext)) {
      return null; // Not in scope = treat as not found
    }
  }

  // Scope validation: check createdBy / assignedField matches userId
  if (scope && scope.type !== "all") {
    const isOwn = record.createdBy === scope.userId;
    const isAssigned = scope.assignedField && record[scope.assignedField] === scope.userId;

    if (scope.type === "own" && !isOwn) {
      return null;
    }
    if (scope.type === "own_and_assigned" && !isOwn && !isAssigned) {
      return null;
    }
    if (scope.type === "assigned_only") {
      // If no assignedField configured, fallback to own check
      if (!scope.assignedField) {
        if (!isOwn) return null;
      } else if (!isAssigned) {
        return null;
      }
    }
  }

  return record;
}
